import React, { useEffect, useState } from 'react'
import { IconLocationPin } from '../../../shared/icon/icon'
import { getBlobAsync } from '../../../shared/api.service'

// The approve screen's map of where the sign-in request came from: auth's
// image (GET /api/v1/auth-device/:code/map?theme=light|dark, 2026-10-08), a
// PNG for the code this session holds (the /find lookup binds it), 404
// otherwise. Auth places the request's IP with DB-IP and fetches the map
// from Mapbox with a server token, so the browser never calls Mapbox and
// holds no Mapbox token (Safari's stripped Referer made the URL-restricted
// one a 403). The image needs the session, so it is fetched with it and
// shown from an object URL.

const isDark = () => document.body.getAttribute('data-bs-theme') === 'dark'
const themeOf = dark => (dark ? 'dark' : 'light')

// Track the admin's light/dark theme so the map follows it live.
const useMapTheme = () => {
  const [dark, setDark] = useState(isDark)
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(isDark()))
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-bs-theme'] })
    return () => obs.disconnect()
  }, [])
  return themeOf(dark)
}

// code|theme -> the promise of its object URL, shared by the preload and the
// map. Codes live two minutes, so a handful at most; each is released when
// another code is asked for.
const maps = new Map()

// Tests start from nothing.
export const forgetDeviceMaps = () => {
  maps.forEach(p => p.then(url => url && URL.revokeObjectURL(url), () => {}))
  maps.clear()
}

const mapURL = (code, theme) => {
  const key = `${code}|${theme}`
  if (!maps.has(key)) {
    ;[...maps.keys()].filter(k => !k.startsWith(`${code}|`)).forEach(k => {
      maps.get(k).then(url => url && URL.revokeObjectURL(url), () => {})
      maps.delete(k)
    })
    const promise = getBlobAsync(process.env.REACT_APP_API_AUTH, `api/v1/auth-device/${encodeURIComponent(code)}/map?theme=${theme}`)
      .then(res => URL.createObjectURL(res.data))
    // a failure is forgotten, so a later mount asks again
    promise.catch(() => maps.get(key) === promise && maps.delete(key))
    maps.set(key, promise)
  }
  return maps.get(key)
}

// Starts the map's fetch the moment the lookup returns, well before the
// confirm screen (and DeviceMap) mounts. Safe to call speculatively: a
// failure is DeviceMap's to show (no map).
export const preloadDeviceMap = code => {
  if (!code) return
  mapURL(code, themeOf(isDark())).catch(() => {})
}

// A skeleton reserves the box the instant this mounts so nothing shifts; the
// image fades in once loaded. No map (auth answered 404, the fetch or the
// image failed, or no code): the location in words only -- no box, never a
// broken image -- and the confirm card is never blocked either way.
export const DeviceMap = ({ code, location }) => {
  const theme = useMapTheme()
  const [src, setSrc] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(!code)

  useEffect(() => {
    setLoaded(false)
    setSrc(null)
    if (!code) {
      setFailed(true)
      return undefined
    }
    setFailed(false)
    let live = true
    mapURL(code, theme).then(
      url => live && setSrc(url),
      () => live && setFailed(true)
    )
    return () => {
      live = false
    }
  }, [code, theme])

  return (
    <div className="ld-map-wrap">
      {!failed &&
        <div className="ld-map" data-testid="ld-map">
          {!loaded && <div className="ld-map__placeholder ld-map__placeholder--skeleton" aria-hidden="true" data-testid="ld-map-skeleton" />}
          {src &&
            <img
              className={`ld-map__img ${loaded ? 'is-loaded' : ''}`}
              data-testid="ld-map-img"
              src={src}
              alt=""
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />}
          <IconLocationPin className="ld-map__marker" />
        </div>}
      <div className="ld-map__caption">
        <b>{location}</b> <span className="ld-map__caption-dim">· approximate</span>
      </div>
    </div>
  )
}
